import { useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { Zap, ChevronLeft, ChevronRight } from 'lucide-react';
import { useRoiCalculator, formatEuro } from './RoiCalculator';
import { useCountUp } from '../lib/useCountUp';
import { fadeUp } from '../lib/dashboardMotion';

type ReportForRoi = { statusColor: string; startDate: string | null };

/** Kompakte Dashboard-Kachel, gleiche Außenmaße wie die übrigen StatCards —
 * Kundenwunsch 14.10.2026: Zahlen direkt eintippbar statt hinter einem Werte-Dialog
 * (ersetzt die vorherige "Potenzial"-Kachel, die nur eine Schätzung war).
 * "Berichte" (abgeschlossene Berichte, Jahr wählbar) kommt aus den echten Berichtsdaten
 * (Dashboard kennt die Zahl bereits) statt manuell eingetragen zu werden — nur die 3
 * Annahmen bleiben editierbar. Jahresfilter < Jahr/Gesamt > wie bei der Umsatz-Kachel. */
export default function RoiStatCard({ darkMode, allReports, ready }: { darkMode: boolean; allReports: ReportForRoi[]; ready: boolean }) {
  const roi = useRoiCalculator();
  const prefersReducedMotion = useReducedMotion();
  const [jahr, setJahr] = useState<number | 'alle'>('alle');
  const muted = darkMode ? 'text-slate-300' : 'text-slate-600';
  const fieldClass = `w-full px-1 py-0.5 rounded border text-xs font-semibold text-center tabular-nums [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
    darkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900'
  }`;
  const resultClass = `rounded-lg px-2 py-2 text-center ${darkMode ? 'bg-slate-700/40' : 'bg-slate-50'}`;
  const staticFieldClass = `w-full px-1 py-0.5 rounded border text-xs font-semibold text-center tabular-nums ${
    darkMode ? 'bg-slate-900/40 border-slate-700 text-slate-300' : 'bg-slate-100 border-slate-200 text-slate-500'
  }`;
  const jahre = useMemo(() => {
    const set = new Set<number>();
    for (const r of allReports) {
      if (r.startDate) set.add(new Date(r.startDate).getFullYear());
    }
    return [...set].sort((a, b) => b - a);
  }, [allReports]);

  const berichteAbgeschlossen = useMemo(() => {
    const anzahl = allReports.filter(r =>
      r.statusColor === 'green' && (jahr === 'alle' || (r.startDate && new Date(r.startDate).getFullYear() === jahr))
    ).length;
    return Math.max(1, anzahl);
  }, [allReports, jahr]);

  const fields = [
    { label: 'Std. ohne', value: roi.roiHoursWithout, setter: roi.setRoiHoursWithout, min: 0, max: 40, step: 0.5 },
    { label: 'Std. mit', value: roi.roiHoursWith, setter: roi.setRoiHoursWith, min: 0, max: 40, step: 0.25 },
    { label: '€/Std.', value: roi.roiRate, setter: roi.setRoiRate, min: 0, max: 500, step: 5 },
  ];

  const hoursSaved = roi.hoursSavedPerReport * berichteAbgeschlossen;
  const moneySaved = hoursSaved * roi.roiRate;
  const hoursSavedAnimated = useCountUp(hoursSaved, ready);
  const moneySavedAnimated = useCountUp(moneySaved, ready);

  return (
    <motion.div variants={fadeUp(prefersReducedMotion)} className={`rounded-xl border shadow-sm p-4 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
          <Zap className="w-4 h-4" />
        </div>
        <h3 className={`text-sm font-semibold truncate ${darkMode ? 'text-white' : 'text-slate-900'}`}>Ersparnis mit Tool</h3>
        <div className="ml-auto flex-shrink-0 flex items-center gap-1">
          <button
            type="button"
            onClick={() => setJahr(prev => {
              const opts: Array<number | 'alle'> = ['alle', ...jahre];
              const idx = opts.indexOf(prev);
              return idx > 0 ? opts[idx - 1] : prev;
            })}
            disabled={jahr === 'alle'}
            className={`w-5 h-5 flex items-center justify-center rounded ${darkMode ? 'hover:bg-slate-700 text-slate-300' : 'hover:bg-slate-100 text-slate-600'} disabled:opacity-30 disabled:cursor-not-allowed`}
            aria-label="Vorheriges Jahr"
          >
            <ChevronLeft className="w-3 h-3" />
          </button>
          <span className={`text-[11px] font-medium tabular-nums w-10 text-center ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
            {jahr === 'alle' ? 'Gesamt' : jahr}
          </span>
          <button
            type="button"
            onClick={() => setJahr(prev => {
              const opts: Array<number | 'alle'> = ['alle', ...jahre];
              const idx = opts.indexOf(prev);
              return idx < opts.length - 1 ? opts[idx + 1] : prev;
            })}
            disabled={jahre.length === 0 || jahr === jahre[jahre.length - 1]}
            className={`w-5 h-5 flex items-center justify-center rounded ${darkMode ? 'hover:bg-slate-700 text-slate-300' : 'hover:bg-slate-100 text-slate-600'} disabled:opacity-30 disabled:cursor-not-allowed`}
            aria-label="Nächstes Jahr"
          >
            <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-1.5 mb-3">
        <div className="flex flex-col gap-0.5 min-w-0">
          <span className={`text-[9px] leading-tight truncate ${muted}`} title="Abgeschlossene Berichte im gewählten Zeitraum, aus euren eigenen Daten">Berichte</span>
          <div className={staticFieldClass}>{berichteAbgeschlossen}</div>
        </div>
        {fields.map((f) => (
          <label key={f.label} className="flex flex-col gap-0.5 min-w-0">
            <span className={`text-[9px] leading-tight truncate ${muted}`}>{f.label}</span>
            <input
              type="number"
              min={f.min}
              max={f.max}
              step={f.step}
              value={f.value}
              onChange={(e) => {
                const n = Number(e.target.value);
                f.setter(Number.isFinite(n) && n >= 0 ? n : 0);
              }}
              className={fieldClass}
            />
          </label>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <div className={resultClass}>
          <div className={`text-[9px] uppercase tracking-wide ${muted}`}>Zeit gespart</div>
          <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>{hoursSavedAnimated} h</div>
        </div>
        <div className={resultClass}>
          <div className={`text-[9px] uppercase tracking-wide ${muted}`}>Geld gespart</div>
          <div className="text-lg font-bold text-emerald-500">{formatEuro(moneySavedAnimated)}</div>
        </div>
      </div>
    </motion.div>
  );
}
